import React, { useCallback, useEffect, useState } from 'react';
import { User, Bot, Scale, TrendingUp, RefreshCw, Wallet, FileWarning } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClientProfilePanel = ({ authFetch }) => {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('neuralia369');
  const [ficha, setFicha] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadClients = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/client-profile/clients`);
      const j = await r.json();
      setClients(j.clients || []);
    } catch (e) { /* noop */ }
  }, [authFetch]);

  const loadFicha = useCallback(async (cid) => {
    try {
      setLoading(true);
      const r = await authFetch(`${API}/client-profile/${encodeURIComponent(cid)}`);
      const j = await r.json();
      if (!r.ok) throw new Error('No se pudo cargar la ficha');
      setFicha(j);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { loadClients(); }, [loadClients]);
  useEffect(() => { loadFicha(clientId); }, [clientId, loadFicha]);

  const mp = ficha?.mentor_plan;

  return (
    <section className="space-y-6" data-testid="client-profile-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-text-muted">Vista 360 · Cliente</p>
          <h3 className="mt-1 text-lg font-bold text-text-heading">Ficha de Cliente</h3>
          <p className="mt-1 text-xs text-text-muted">Plan de mentores, agentes activos y último brief de mercado en una sola vista.</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="profile-client-select" value={clientId} onChange={(e) => setClientId(e.target.value)}
            className="rounded-xl border border-border-subtle bg-background/70 p-2.5 text-sm text-text-heading focus:border-primary focus:outline-none">
            {!clients.includes(clientId) && <option value={clientId}>{clientId}</option>}
            {clients.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button type="button" onClick={() => loadFicha(clientId)} disabled={loading} data-testid="profile-refresh"
            className="inline-flex items-center gap-2 rounded-full border border-border-subtle px-4 py-2 text-xs font-semibold text-text-muted transition hover:text-text-heading">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </button>
        </div>
      </div>

      {/* Cabecera resumen */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="glass-card p-5" data-testid="profile-stat-agents">
          <div className="flex items-center justify-between gap-3">
            <div><p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Agentes activos</p><p className="mt-3 text-3xl font-bold text-text-heading">{ficha?.active_agents_count ?? 0}</p></div>
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><Bot className="h-5 w-5" /></div>
          </div>
        </div>
        <div className="glass-card p-5" data-testid="profile-stat-plan">
          <div className="flex items-center justify-between gap-3">
            <div><p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Plan de mentores</p><p className="mt-3 text-3xl font-bold text-text-heading">{mp?.chosen_plan ? `Plan ${mp.chosen_plan}` : '—'}</p></div>
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><Scale className="h-5 w-5" /></div>
          </div>
        </div>
        <div className="glass-card p-5" data-testid="profile-stat-wallet">
          <div className="flex items-center justify-between gap-3">
            <div><p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Saldo inti</p><p className="mt-3 text-3xl font-bold text-text-heading">{ficha?.wallet_inti ?? '—'}</p></div>
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><Wallet className="h-5 w-5" /></div>
          </div>
        </div>
        <div className="glass-card p-5" data-testid="profile-stat-pending">
          <div className="flex items-center justify-between gap-3">
            <div><p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Cobros pendientes</p><p className="mt-3 text-3xl font-bold text-text-heading">{ficha?.pending_payments ?? 0}</p></div>
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><FileWarning className="h-5 w-5" /></div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Plan de mentores */}
        <div className="glass-card p-5" data-testid="profile-mentor-plan">
          <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-heading"><Scale className="h-4 w-4 text-primary" /> Plan de mentores vigente</h4>
          {mp ? (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-text-heading">Plan {mp.chosen_plan} · {mp.plan?.title || mp.topic}</p>
              <p className="text-xs text-text-muted">{mp.plan?.summary}</p>
              <ol className="list-decimal space-y-1 pl-5 text-[11.5px] text-text-muted">
                {(mp.plan?.steps || []).map((s, i) => <li key={i}>{s}</li>)}
              </ol>
              <p className="text-[10px] text-text-muted">Decidido: {mp.decided_at ? new Date(mp.decided_at).toLocaleString('es-PE') : '—'} · {mp.history_count} decisión(es) en historial</p>
            </div>
          ) : <p className="text-sm text-text-muted" data-testid="profile-no-plan">Sin plan de mentores decidido. Genera un dictamen en la pestaña Mentores IA.</p>}
        </div>

        {/* Agentes activos */}
        <div className="glass-card p-5" data-testid="profile-agents">
          <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-heading"><Bot className="h-4 w-4 text-primary" /> Agentes activos</h4>
          <div className="space-y-2">
            {(ficha?.active_agents || []).map((a) => (
              <div key={a.installation_id} className="flex items-center justify-between gap-3 rounded-xl border border-border-subtle bg-background/50 p-3" data-testid={`profile-agent-${a.installation_id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-text-heading">{a.install_name || a.template_name}</p>
                  <p className="truncate text-xs text-text-muted">{a.sector} · {a.activated_at ? new Date(a.activated_at).toLocaleDateString('es-PE') : ''}</p>
                </div>
              </div>
            ))}
            {!(ficha?.active_agents || []).length && <p className="text-sm text-text-muted" data-testid="profile-no-agents">Sin agentes activos.</p>}
          </div>
        </div>
      </div>

      {/* Último brief de mercado */}
      <div className="glass-card p-5" data-testid="profile-market-brief">
        <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-heading"><TrendingUp className="h-4 w-4 text-primary" /> Último brief de mercado</h4>
        {ficha?.market_brief ? (
          <div>
            <p className="mb-2 text-[10px] text-text-muted">{ficha.market_brief.created_at ? new Date(ficha.market_brief.created_at).toLocaleString('es-PE') : ''}</p>
            <ul className="space-y-1.5 text-xs text-text-muted">
              {(ficha.market_brief.summary_lines || []).map((l, i) => <li key={i} className="leading-relaxed">{l}</li>)}
            </ul>
          </div>
        ) : <p className="text-sm text-text-muted" data-testid="profile-no-brief">Sin brief de mercado. Genera uno en Panel Mercado.</p>}
      </div>
    </section>
  );
};

export default ClientProfilePanel;
